import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';

import fields from '../../lib/fields';

import FormFields from '../../components/form/fields';
import ImageScroller from '../../components/imagescroller';
import HelpText from '../../components/gui/helpText';
import FileList from '../../components/fileViewer/list';
import Input from '../../components/form/input';
import LoadingSpinner from '../../components/loadingSpinner';

// Access members at render time only (componentsOther.X) — this registry sits
// in an import cycle and is not fully evaluated at module scope.
import componentsOther from '../../product/components-other';

import localUtil from '../../lib/util';

const MARKDOWN_OPTIONS = {
	pedantic: false,
	gfm: true,
	breaks: true,
	sanitize: false,
	smartLists: true,
	smartypants: true,
	xhtml: true,
    highlight: function(code, lang) {
        const hljs = require('highlight.js');
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
      },
    langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

function mediaImg(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3LargeLink}`;
    return imgSrc;
}

@observer
class MediaView extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
            sections: fields.get('news'),
            aiRewriting: false,
            aiSuggestion: null,
            showAiSuggestion: false,
            saving: false,
            saved: false,
        };
        this.mainContainer = null;
    }

    loadAll = async (props = this.props) => {
        const { id } = props;
        const { mediaStore, newsStore } = this.props.stores;
        mediaStore.updateKeyValue('media', {});
        await mediaStore.load(id, false, { addData: ['tags'] });
        await newsStore?.load({ query: {}, addData: ['races'] });

        const { media } = mediaStore;
        if (!media || !media.id) {
            return;
        }
        newsStore?.updateKeyValue('newNews', {
            title: media.title,
            body: media.body,
            tag: 'raceNews',
            images: [...(media.images || [])],
            files: [...(media.files || [])],
            race: media.race,
            tags: [...(media.tags || [])],
            publishedToNews: 1,
        });
    }

    // editMedia = () => {
	// 	const { id, drawerLevel } = this.props;
	// 	const { appState } = this.props.stores;
	// 	const { drawerHeightMedium } = appState;
	// 	appState.openDrawer('editMedia', {
	// 		height: drawerHeightMedium,
	// 		id,
	// 	}, drawerLevel + 1);
	// }

    addTag = e => {
        const { tagInput = '' } = this.state;
        const { mediaStore } = this.props.stores;
        const value = {
            name: tagInput.toLowerCase().trim(),
            date: new Date(),
        };
        value.md5 = md5(JSON.stringify(value)).toString();

        const field = 'tags';
        const object = mediaStore.media;
        const tagExists = object[field] && object[field].find(tag => tag.name === value.name);
        if (!tagExists) {
            const localValue = object[field] ? [...object[field]] : [];
            mediaStore.saveField(object.id, field, value);
            mediaStore.updateField(object.id, field, [...localValue, value]);
        }
        this.setState({ tagInput: '' });
    }

    checkForEnter = (e) => {
        if (e.key === 'Enter') {
            this.addTag();
        }
    }

    publishNews = async () => {
        const { newsStore, mediaStore } = this.props.stores;
        const { newNews = {} } = newsStore || {};
        const { media } = mediaStore;
        const { id } = media;

        this.setState({ publishing: true });

        // Ensure active is set to 1 for publishing
        const newsToPublish = { ...newNews, active: 1 };
        if (!newsStore) { this.setState({ publishing: false }); return; }
        const response = await newsStore.insert(newsToPublish); // store-guarded (early return above)
        const news = response?.data;
        if (news) {
            await mediaStore.saveField(id, 'publishedToNews', 1);
            await mediaStore.updateField(id, 'publishedToNews', 1);
            this.setState({ publishing: false, published: true });
            // route(`/news/${news.id}`);
        } else {
            this.setState({ publishing: false });
        }
    }

    saveForLater = async () => {
        const { newsStore, mediaStore } = this.props.stores;
        const { newNews = {} } = newsStore || {};
        const { media } = mediaStore;
        const { id } = media;

        this.setState({ saving: true });

        // Save changes to current media for later publishing
        const updates = {
            id,
            title: newNews.title,
            body: newNews.body,
            race: newNews.race,
            tags: newNews.tags,
        };
        await mediaStore.save(updates);
        this.setState({ saving: false, saved: true });
    }

    archiveMedia = async () => {
        const { mediaStore, appState } = this.props.stores;
        const { media } = mediaStore;
        const { id } = media;
        const { callback = () => {} } = this.props;

        this.setState({ archiving: true });
        await mediaStore.saveField(id, 'offline', 1);
        await mediaStore.updateField(id, 'offline', 1);
        this.setState({ archiving: false, archived: true });

        // Close drawer and refresh list
        setTimeout(() => {
            callback();
        }, 500);
    }

    aiRewriteBody = async () => {
        const { mediaStore, newsStore } = this.props.stores;
        const { newNews = {} } = newsStore || {};
        const { media } = mediaStore;

        const bodyText = newNews.body || media.body || '';
        const title = newNews.title || media.title || '';

        if (!bodyText || bodyText.trim().length === 0) {
            return;
        }

        this.setState({ aiRewriting: true, aiSuggestion: null, showAiSuggestion: false });

        try {
            const response = await mediaStore.rewriteBody(bodyText, title, {
                race: newNews.race || media.race,
                tags: newNews.tags || media.tags,
            });

            const rewrittenBody = response?.data?.rewrittenBody || response?.rewrittenBody;
            if (rewrittenBody) {
                this.setState({
                    aiSuggestion: rewrittenBody,
                    showAiSuggestion: true,
                });
            }
        } catch (error) {
            console.error('AI rewrite error:', error);
        } finally {
            this.setState({ aiRewriting: false });
        }
    }

    aiReplaceBody = () => {
        const { newsStore } = this.props.stores;
        const { aiSuggestion } = this.state;

        if (aiSuggestion) {
            newsStore?.updateObjectKeyValue('newNews', 'body', aiSuggestion);
            this.setState({ aiSuggestion: null, showAiSuggestion: false });
        }
    }

    aiInsertBody = () => {
        const { newsStore } = this.props.stores;
        const { newNews = {} } = newsStore || {};
        const { aiSuggestion } = this.state;

        if (aiSuggestion) {
            const currentBody = newNews.body || '';
            const newBody = currentBody ? `${currentBody}\n\n${aiSuggestion}` : aiSuggestion;
            newsStore?.updateObjectKeyValue('newNews', 'body', newBody);
            this.setState({ aiSuggestion: null, showAiSuggestion: false });
        }
    }

    aiDismiss = () => {
        this.setState({ aiSuggestion: null, showAiSuggestion: false });
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.page !== this.props.page || nextProps.artid !== this.props.artid) {
            this.loadAll(nextProps);
        }
    }

    componentWillUnmount() {

    }

    render() {
        const {
            page,
            artid,
            bib,
            showAnimations = true,
            drawerLevel = 1,
            lookupArray,
        } = this.props;
        const {
            sessionid,
            showMenu,
            tagInput,
            sections,
            publishing,
            published,
            archiving,
            archived,
            aiRewriting,
            aiSuggestion,
            showAiSuggestion,
            saving,
            saved,
        } = this.state;
        const { appState, userStore, mediaStore, newsStore } = this.props.stores;
        const { mainView, subView, isDevelopment, path } = appState;
        const { user = {}, isAdmin, isTester, isVeterinary, language } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { media } = mediaStore;

        const linkTextClass = `${darkmode ? 'text-white' : 'text-dark'}`;
		const linkTextStyle = `line-height: 24px; font-size: 21px; font-weight: 400;`;

        const { isMediaWorker } = user;
        if (!isMediaWorker) {
            return(<>
                <div class='w-100 h-100 d-flex justify-content-center'>
                    <div class='w-100 h-100 d-flex flex-column justify-content-center align-items-center' style='padding-top: 60px; padding-bottom: 200px;'>
                        <div class='display-1 text-warning'>
                            <i class='fa-solid fa-exclamation-triangle' />
                        </div>
                        <div style='font-size: 1.5em;'>
                            <Text id='admin.only'>No access!</Text>
                        </div>
                    </div>
                </div>
            </>);
        }

        if (!media || !media.id) {
            return (<LoadingSpinner />);
        }

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <div class={`d-flex justify-content-start flex-row w-100`}>
                        <div class='flex-fill'>
                            {/* <h5 class='ms-3 mb-3'>{media.title}</h5> */}
                        </div>
                        {/* <div class='' style='margin-right: 65px;'>
                            <span onClick={this.editMedia} class={`text-primary ${linkTextClass}`} style={`${linkTextStyle}`}>
                                <i class={`fa-regular fa-pen ms-3 me-1`} />
                            </span>
                        </div> */}
                    </div>

                    <div class='w-100 d-flex flex-column'>
                        <div class='px-0 box-header-info d-flex flex-column'>
                            <div class='d-flex'>
                                {/* {JSON.stringify(media)} */}
                                <ImageScroller images={media.images} stores={this.props.stores} showImg={img => mediaImg(img, this.props, '1920x')} showImageInfo={true} />
                            </div>

                            {media.files?.length > 0 && media.files.map(file => {
                                const poster = file.s3LinkStreamPreview1;

                                // console.log({ file });
                                if (file.s3LinkStream && componentsOther.VideoStreamingPlayer) {
                                    return (<>
                                        <div class='video-container rounded-lg'>
                                            <componentsOther.VideoStreamingPlayer
                                                key={file.s3LinkStream}
                                                videoStreamUrl={file.s3LinkStream}
                                                stores={this.props.stores}
                                                poster={poster}
                                                transcript={file.transcript}
                                                transcriptionSegments={file.transcriptionSegments}
                                                transcriptionLanguage={file.transcriptionLanguage}
                                                transcriptEnglish={file.transcriptEnglish}
                                                transcriptionSegmentsEnglish={file.transcriptionSegmentsEnglish}
                                                transcriptSpanish={file.transcriptSpanish}
                                                transcriptionSegmentsSpanish={file.transcriptionSegmentsSpanish}
                                                transcriptFrench={file.transcriptFrench}
                                                transcriptionSegmentsFrench={file.transcriptionSegmentsFrench}
                                                transcriptFinnish={file.transcriptFinnish}
                                                transcriptionSegmentsFinnish={file.transcriptionSegmentsFinnish}
                                            />
                                        </div>
                                        <div class='video-info d-flex flex-row justify-content-center'>
                                            <span class='text-muted'>size:</span> {localUtil.formatFileSize(file.size)}
                                            <span class='text-muted ms-3 me-1'>encoding:</span> {file.encoding}
                                            <span class='text-muted ms-3 me-1'>mimetype:</span> {file.mimetype}
                                            <span class='text-muted ms-3 me-1'>ext:</span> {file.ext}
                                            {/* <span class='text-muted ms-3 me-1'>s3Link:</span> <a href={file.s3Link} target='_blank' rel='noopener noreferrer'>download</a> */}
                                        </div>
                                    </>);
                                }
                                if (file.s3Link.match(/\.(m4v|m4a|mp4)$/i)) {
                                    return (<>
                                        <div className='video-container rounded-lg'>
                                            <video controls className='video-player'>
                                                <source src={file.s3Link} type='video/mp4' />
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                        <div class='video-info d-flex flex-row justify-content-center'>
                                            <span class='text-muted'>size:</span> {localUtil.formatFileSize(file.size)}
                                            <span class='text-muted ms-3 me-1'>encoding:</span> {file.encoding}
                                            <span class='text-muted ms-3 me-1'>mimetype:</span> {file.mimetype}
                                            <span class='text-muted ms-3 me-1'>ext:</span> {file.ext}
                                            <span class='text-muted ms-3 me-1'>s3Link:</span> <a href={file.s3Link} target='_blank' rel='noopener noreferrer'>download</a>
                                        </div>
                                    </>);
                                }
                                if (file.s3Link.match(/\.(mov)$/i)) {
                                    return (<>
                                        <div className='video-container rounded-lg'>
                                            <video controls className='video-player'>
                                                <source src={file.s3Link} type='video/quicktime' />
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                        <div class='video-info d-flex flex-row justify-content-center'>
                                            <span class='text-muted'>size:</span> {localUtil.formatFileSize(file.size)}
                                            <span class='text-muted ms-3 me-1'>encoding:</span> {file.encoding}
                                            <span class='text-muted ms-3 me-1'>mimetype:</span> {file.mimetype}
                                            <span class='text-muted ms-3 me-1'>ext:</span> {file.ext}
                                            <span class='text-muted ms-3 me-1'>s3Link:</span> <a href={file.s3Link} target='_blank' rel='noopener noreferrer'>download</a>
                                        </div>
                                    </>);
                                }
                            })}

                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                                {[
                                    'offline',
                                    'title',
                                    'body',
                                    'whoIsInTheMedia',
                                    'whatIsInTheMedia',
                                    'whatIsTheStory',
                                    'whereIsTheMediaRecorded',
                                    'whenIsTheMediaRecorded',
                                    'preferredUse',
                                    'bib',
                                    'attribution',
                                    'licenseType',
                                    'userAgreement',
                                ].map(key => {
                                    if (util.isUndefined(media[key])) {
                                        return '';
                                    }
                                    const field = fields.getField('media', key);
                                    if (!field) {
                                        console.log('Missing field:', key);
                                    }
                                    const displayValue = field.displayValue || (value => value);
                                    return (<>
                                        {(media[key] || media[key] === false) && <div class='d-flex flex-row'>
                                            <span class='text-muted font-weight-light'>
                                                {field.title}
                                            </span>
                                            {field.type === 'textarea' ? <>
                                                <div class='ms-2 mb-4 text-wrap'>
                                                    <Markdown markdown={displayValue(media[key])} markedOpts={MARKDOWN_OPTIONS} />
                                                </div>
                                            </> : <>
                                                <span class='ms-2'>
                                                    {field.type === 'toggle' ? <>
                                                        {media[key] ? <>
                                                            <i class='fa-solid fa-check text-success' />
                                                        </> : <>
                                                            <i class='fa-solid fa-times text-danger' />
                                                        </>}
                                                    </> : <>
                                                        {displayValue(media[key])}
                                                    </>}
                                                </span>
                                            </>}
                                        </div>}
                                    </>);
                                })}
                            </div>

                            <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                {media.files?.length > 0 && <div class='d-flex flex-column'>
                                    <span class='font-weight-lighter text-muted'>
                                        <Text id='media.files'>Files</Text>
                                    </span>
                                    <div class={`d-flex flex-column`}>
                                        <FileList files={media.files} stores={this.props.stores} />
                                    </div>
                                </div>}

                                <Input
                                    stores={this.props.stores}
                                    holdingStore={mediaStore}
                                    field='tags'
                                    object={media}
                                    objectName='media'
                                    type='tags'
                                    lookupArray={'tags'}
                                />

                            </div>
                            <HelpText>
                                <Text id='media.tag-help'>Add tags to this media to make it easier to find and organize. Tip: Use general tags like: photo, fishing, car, boat...</Text>
                            </HelpText>

                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                                {[
                                    'publishedToWeb',
                                    'publishedToNews',
                                    'publishedToFacebook',
                                    'publishedToInstagram',
                                    'publishedToOther',
                                    'done',
                                ].map(key => {
                                    const field = fields.getField('media', key);
                                    if (!field) {
                                        console.log('Missing field:', key);
                                    }
                                    const displayValue = field.displayValue || (value => value);
                                    return (<>
                                        {media[key] > 0 && <div class='d-flex flex-row'>
                                            <span class='text-muted font-weight-light'>
                                                {key}
                                            </span>
                                            {field.type === 'textarea' ? <>
                                                <div class='ms-2 mb-4 text-wrap'>
                                                    <Markdown markdown={displayValue(media[key])} markedOpts={MARKDOWN_OPTIONS} />
                                                </div>
                                            </> : <>
                                                <span class='ms-2'>
                                                    {displayValue(media[key])}
                                                </span>
                                            </>}
                                        </div>}
                                    </>);
                                })}
                            </div>

                            {newsStore && <>
                            <div class='font-weight-lighter px-3 box-header mt-5'>
                                Publish to race
                            </div>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                                <FormFields
                                    stores={this.props.stores}
                                    sections={sections}
                                    fields={['title', 'body', 'race', 'tags', 'active']}
                                    holdingStore={newsStore} // store-guarded (newsStore && gate above)
                                    objectName={'newNews'}
                                    isNew={true}
                                    drawerLevel={drawerLevel}
                                    isAdmin={false}
                                    hideAdmin={true}
                                    lookupArray={lookupArray}
                                />

                                {/* AI Rewrite Button */}
                                <div class='d-flex flex-column mt-2 mb-3'>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-secondary rounded-pill'
                                        onClick={this.aiRewriteBody}
                                        disabled={aiRewriting}
                                        style='width: fit-content;'
                                    >
                                        {aiRewriting ? <>
                                            <i class='fa-duotone fa-spinner-third fa-spin me-2' />
                                            Skriver om...
                                        </> : <>
                                            <i class='fa-solid fa-wand-magic-sparkles me-2' />
                                            AI Skriv om
                                        </>}
                                    </button>
                                </div>

                                {/* AI Suggestion Panel */}
                                {showAiSuggestion && aiSuggestion && <>
                                    <div class={`d-flex flex-column p-3 mb-3 rounded-lg ${darkmode ? 'bg-dark' : 'bg-light'} border`}>
                                        <div class='d-flex align-items-center mb-2'>
                                            <i class='fa-solid fa-lightbulb text-warning me-2' />
                                            <span class='font-weight-bold'>AI Forslag:</span>
                                        </div>
                                        <div class='mb-3' style='max-height: 200px; overflow-y: auto; white-space: pre-wrap;'>
                                            {aiSuggestion}
                                        </div>
                                        <div class='d-flex flex-row gap-2'>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-success rounded-pill'
                                                onClick={this.aiReplaceBody}
                                            >
                                                <i class='fa-solid fa-check me-1' />
                                                Erstatt
                                            </button>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-primary rounded-pill'
                                                onClick={this.aiInsertBody}
                                            >
                                                <i class='fa-solid fa-plus me-1' />
                                                Sett inn
                                            </button>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                                onClick={this.aiDismiss}
                                            >
                                                <i class='fa-solid fa-times me-1' />
                                                Avbryt
                                            </button>
                                        </div>
                                    </div>
                                </>}

                                <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3'>
                                    {published ? <>
                                        <div class='d-flex flex-row justify-content-center align-items-center'>
                                            <i class='fa-duotone fa-check text-success me-2' />
                                            <Text id='news.published'>Published</Text>
                                        </div>
                                    </> : saved ? <>
                                        <div class='d-flex flex-row justify-content-center align-items-center'>
                                            <i class='fa-duotone fa-check text-success me-2' />
                                            <Text id='news.saved'>Lagret som kladd</Text>
                                        </div>
                                    </> : <>
                                        <button
                                            disabled={publishing || saving}
                                            type='button'
                                            class='btn btn-block btn-primary rounded-pill'
                                            onClick={this.publishNews}
                                        >
                                            {publishing && <i class='fa-duotone fa-spinner-third fa-spin me-2' />}
                                            <i class='fa-solid fa-paper-plane me-2' />
                                            <Text id='news.publish'>Publiser nyheten</Text>
                                        </button>
                                        <button
                                            disabled={publishing || saving}
                                            type='button'
                                            class='btn btn-block btn-outline-secondary rounded-pill mt-2'
                                            onClick={this.saveForLater}
                                        >
                                            {saving && <i class='fa-duotone fa-spinner-third fa-spin me-2' />}
                                            <i class='fa-solid fa-floppy-disk me-2' />
                                            <Text id='news.saveForLater'>Lagre som kladd</Text>
                                        </button>
                                        <div class='text-muted small mt-2 text-center'>
                                            <Text id='news.saveForLater-help'>Kladd er ikke synlig i feeden</Text>
                                        </div>
                                    </>}
                                </div>

                            </div>
                            </>}

                            {/* Archive button */}
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-5`}>
                                <div class='font-weight-lighter text-muted mb-2'>
                                    <Text id='media.archive-section'>Arkiv</Text>
                                </div>
                                {archived ? <>
                                    <div class='d-flex flex-row justify-content-center align-items-center text-success'>
                                        <i class='fa-solid fa-check me-2' />
                                        <Text id='media.archived'>Arkivert</Text>
                                    </div>
                                </> : <>
                                    <button
                                        disabled={archiving}
                                        type='button'
                                        class='btn btn-block btn-danger rounded-pill'
                                        onClick={this.archiveMedia}
                                    >
                                        {archiving ? <>
                                            <i class='fa-duotone fa-spinner-third fa-spin me-2' />
                                        </> : <>
                                            <i class='fa-solid fa-box-archive me-2' />
                                        </>}
                                        <Text id='media.archive'>Arkiver media</Text>
                                    </button>
                                    <div class='text-muted small mt-2 text-center'>
                                        <Text id='media.archive-help'>Arkivering fjerner media fra listen, men sletter det ikke.</Text>
                                    </div>
                                </>}
                            </div>

                        </div>
                    </div>

                    {/* <SimpleBlog stores={this.props.stores} {...this.props} /> */}
                </div>
            </div>
        </>);
    }
}

export default MediaView;
